import { restartableTask } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
// @ts-ignore
import BomPartCodeCell from 'volta/components/bom/tree-table/part-code-cell';
// @ts-ignore
import BufferIconCell from 'volta/components/cell/buffer-icon-cell';
// @ts-ignore
import LabelCell from 'volta/components/cell/label-cell';
import SkuModel, { SkuResourceName, SkuQueries } from 'volta/models/sku';
import StoreService from 'volta/services/store';
import LabelService from 'volta/services/label-service';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  skuId: string;
  baseSkuPath: string;
}

export default class SkuFinishedProducts extends Component<IArgs> {
  @service store!: StoreService;
  @service intl!: IntlService;
  @service labelService!: LabelService;

  @tracked finishedProducts: SkuModel[] = [];
  @tracked sorts = [{ valuePath: 'partCode', isAscending: true }];
  @tracked selectedItems: SkuModel[] = [];
  resourceName = SkuResourceName;

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  @sort('finishedProducts', 'sortProperties')
  sortedFinishedProducts!: SkuModel[];

  get columns() {
    const { baseSkuPath } = this.args;
    const { intl } = this;

    return [
      {
        columnName: intl.t('source'),
        valuePath: 'sku.warehouse.code',
        isResizable: true,
        isReorderable: false,
        minWidth: 75,
        width: 100,
        isFixed: 'left',
        cellComponent: LabelCell,
        format: (sku: SkuModel) => {
          return { ...sku.sourcedFrom, label: sku.sourcedFrom.code };
        }
      },
      {
        columnName: intl.t('part'),
        valuePath: 'partCode',
        sortKey: 'partCode',
        isResizable: true,
        width: 200,
        cellComponent: BomPartCodeCell,
        format: (model: SkuModel) => {
          return {
            code: model.partCode,
            description: model.skuDescription,
            link: baseSkuPath || 'protected.plannings.sku',
            linkParam: model.skuId,
            cellClasses: model.isActive ? undefined : 'u-text-strike',
            endDate: model.deactivatedAt
          };
        }
      },
      {
        columnName: '',
        valuePath: 'methodology',
        sortKey: 'methodology',
        isResizable: false,
        isReorderable: true,
        width: 35,
        cellComponent: BufferIconCell,
        format: (model: SkuModel) => {
          return {
            methodology: model.methodology,
            isProtected: model.isProtected
          };
        }
      },

      {
        columnName: intl.t('acqCodeShort'),
        valuePath: 'acqCode',
        width: 90,
        isReorderable: true,
        isResizable: false,
        cellComponent: LabelCell,
        format: (model: SkuModel) => {
          return {
            label: intl.t(`acqCodes.${model.acqCode || 'UNKNOWN'}`)
          };
        }
      },
      {
        columnName: this.intl.t('leadTimes'),
        valuePath: 'vltDisplay',
        sortKey: 'voltaLeadTime',
        classNames: 'u-text-mono',
        textAlign: 'right',
        isResizable: true,
        isReorderable: true,
        minWidth: 25,
        width: 80
      }
    ];
  }

  getFinishedProductsTask = restartableTask(async (skuId: string) => {
    try {
      const response: any[] = await this.store.customQuery(
        SkuResourceName.singular,
        SkuQueries.FinishedProducts,
        skuId
      );
      this.finishedProducts = normalize(SkuResourceName.singular, response) as SkuModel[];
      return this.finishedProducts;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });
}
